import React, { useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import Navbar, { NAVBAR_HEIGHT } from '../../../components/Navbar';
import {
  ACTIVITY_PAGE_TYPE,
  ActivityLayout,
} from '../../../components/Activity';
import { ACTIVITY_TYPE, ActivityProvider } from '../../../contexts/activity';
import { ROUTES } from '../../../routing';
import { CmsProjectDefinitionSchema } from '../../../schemas/applied-project';
import type { CmsProjectDefinition } from '../../../schemas/applied-project';
import { cmsProjectToActivityResponse } from '../../../lib/cms-project-to-activity';
import type { ActivityPayload } from '../../../hydra';
import { previewValidateAnswer } from '../../../api/admin';
import useAuth from '../../../hooks/use-auth';
import AIChat from '../../../components/AIChat';
import { buildCmsPreviewChatContext } from '../../../lib/cms-preview-chat-context';
import { AIWorkflowProvider } from '../../../contexts/ai-workflow';

const PREVIEW_STORAGE_KEY = 'enki:preview:project';
const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const StyledAIChat = styled(AIChat)`
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px - 32px);
  bottom: calc(${NAVBAR_HEIGHT}px + 16px);
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);
`;

const FadeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  animation: ${fadeIn} 0.4s ease-in-out forwards;
  height: 100vh;
`;

const StyledNavbar = styled(Navbar)`
  position: static;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  gap: 16px;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 18px;
`;

export default function CMSPreviewProject() {
  const { getTokenSilently } = useAuth();
  const [previewData, setPreviewData] =
    React.useState<ActivityPayload | null>(null);
  const [cmsDefinition, setCmsDefinition] =
    React.useState<CmsProjectDefinition | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const previewDataRef = useRef(previewData);
  React.useEffect(() => {
    previewDataRef.current = previewData;
  }, [previewData]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(PREVIEW_STORAGE_KEY);
      if (!raw) {
        setError(
          'No preview data found. Open the preview from the editor page.'
        );
        return;
      }

      const parsed = JSON.parse(raw);

      if (
        parsed._createdAt &&
        Date.now() - parsed._createdAt > PREVIEW_MAX_AGE_MS
      ) {
        localStorage.removeItem(PREVIEW_STORAGE_KEY);
        setError(
          'Preview data has expired. Open a new preview from the editor.'
        );
        return;
      }

      const result = CmsProjectDefinitionSchema.safeParse(parsed);
      if (!result.success) {
        setError(
          `Invalid preview data: ${result.error.issues
            .map((i) => i.message)
            .join(', ')}`
        );
        return;
      }

      setCmsDefinition(result.data);
      setPreviewData(cmsProjectToActivityResponse(result.data));
    } catch (e) {
      setError(`Failed to load preview data: ${(e as Error).message}`);
    }
  }, []);

  React.useEffect(() => {
    const cleanup = () => localStorage.removeItem(PREVIEW_STORAGE_KEY);
    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
  }, []);

  const activityState = {
    loading: false,
    error: null,
    value: previewData,
    retry: () => {},
  };

  const getActivity = useCallback(async (): Promise<ActivityPayload> => {
    const data = previewDataRef.current;
    if (!data) throw new Error('Preview data is not loaded');
    return data;
  }, []);

  // The preview never persists: submissions are discarded.
  const saveSubmission = useCallback(async () => {}, []);
  const goToHome = useCallback(async () => {}, []);
  const createAIReview = useCallback(async () => {}, []);

  const submitAnswer = useCallback(
    async ({
      questionSlug,
      value,
    }: {
      questionSlug: string;
      value: string;
    }) => {
      if (!cmsDefinition) return;

      const cmsQuestion = cmsDefinition.questions.find(
        (q) => q.slug === questionSlug
      );
      if (!cmsQuestion) return;

      const isAIQuestion =
        cmsQuestion.type === 'LLM_PLAYGROUND' ||
        cmsQuestion.type === 'FREE_TEXT_AI_VERIFIED';
      if (!isAIQuestion) return;

      try {
        const token = await getTokenSilently();
        const result = await previewValidateAnswer(token, {
          questionText: cmsQuestion.text,
          questionNotes: cmsQuestion.notes ?? '',
          questionType: cmsQuestion.type,
          modelAnswer: cmsQuestion.modelAnswer ?? null,
          userAnswer: value,
        });

        setPreviewData((prev) => {
          if (!prev) return prev;
          const nowIso = new Date().toISOString();
          const base = prev.latest_submission ?? {
            id: -1,
            updated_at: nowIso,
            created_at: nowIso,
            difficulty_rating: null,
            feedback: null,
            seconds_spent: null,
            activity_session_id: -1,
            ai_review: null,
            answer_states: {},
            mentor_reviews: null,
          };
          const currentStates = base.answer_states ?? {};
          const updated: ActivityPayload = {
            ...prev,
            latest_submission: {
              ...base,
              answer_states: {
                ...currentStates,
                [questionSlug]: {
                  ...(currentStates[questionSlug] ?? {
                    value,
                    is_correct: null,
                    is_submitted: false,
                    submitted_at: null,
                  }),
                  value,
                  is_submitted: true,
                  submitted_at: new Date(),
                  result: result.result,
                  review: result.review,
                  is_correct: result.isCorrect,
                  does_satisfy_all_requirements:
                    result.doesSatisfyAllRequirements,
                },
              },
            },
          };
          previewDataRef.current = updated;
          return updated;
        });
      } catch (err) {
        console.error('Preview validation failed:', err);
      }
    },
    [cmsDefinition, getTokenSilently]
  );

  const breadCrumbs = [
    { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
    { label: 'Projects', url: ROUTES.ADMIN.CMS_PROJECTS.getPath() },
    { label: previewData?.title ?? 'Project', url: null },
  ];

  return (
    <AIWorkflowProvider>
      <FadeWrapper>
        <StyledNavbar isPreview breadCrumbs={breadCrumbs} />
        {error && <ErrorWrapper>{error}</ErrorWrapper>}
        {previewData && (
          <>
            <Helmet>
              <title>{previewData.title} — Preview</title>
            </Helmet>
            {/* Send the bounded prompt fields for every ordered question, not the
              complete CMS draft. The chat initializes this context once. */}
            <StyledAIChat
              contentId={null}
              missionId={null}
              activityId={null}
              projectId={null}
              discussionContext={'cms-preview-project'}
              previewContext={buildCmsPreviewChatContext(previewData)}
              shouldShowChatIcon={false}
            />
            <ActivityProvider
              activity={previewData}
              type={ACTIVITY_TYPE.PROJECT}
            >
              <ActivityLayout
                type={ACTIVITY_PAGE_TYPE.PREVIEW}
                allowAIChatInPreview
                goToNextActivity={goToHome}
                saveSubmission={saveSubmission}
                getActivity={getActivity}
                createAIReview={createAIReview}
                dashboardUrl={ROUTES.ADMIN.CMS_PROJECTS.getPath()}
                activityState={activityState}
                submitAnswer={submitAnswer}
              />
            </ActivityProvider>
          </>
        )}
      </FadeWrapper>
    </AIWorkflowProvider>
  );
}
